import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Building2, FileCheck2, LayoutGrid, Mail, Search, SquareKanban } from "lucide-react";
import { useAppData, useConfigGuard } from "../../contexts/AppContext";
import { globalSearch, searchKanbanCards, ISearchGroup } from "../../_services/search.service";
import { atividadeK2Path } from "../../kanban/registroDoVinculo";
import { buildDestinations, filterDestinations } from "./searchDestinations";
import { loadedResources } from "../../routes/routeAccess";
import { topNavLayers } from "./topNavLayers";

/**
 * Selo de tecla. Não usa o `<Kbd>` do Chakra: neste tema ele sai com texto BRANCO, então o
 * atalho ficava invisível sobre o fundo claro (medido no QA de 21/08/2026 — `color:
 * rgb(255,255,255)` computado). Aqui as cores são explícitas e não dependem do tema.
 */
const KeyCap = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    px="5px"
    py="1px"
    borderRadius="4px"
    border="1px solid"
    borderColor="#C8C6CA"
    backgroundColor="#F2F0F4"
    color="#46464A"
    fontSize="11px"
    fontFamily="inherit"
    lineHeight="16px"
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

/** Mínimo de caracteres para consultar o backend (o DTO exige 2). */
const MIN_TERM = 2;
/** Espera após a última tecla antes de consultar — evita uma requisição por letra. */
const DEBOUNCE_MS = 300;

interface IFlatRow {
  key: string;
  groupLabel: string;
  title: string;
  subtitle?: string;
  path: string;
  icon: typeof Building2;
}

const GROUP_ICON: Record<string, typeof Building2> = {
  destinations: LayoutGrid,
  companies: Building2,
  certificates: FileCheck2,
  messages: Mail,
  tasks: SquareKanban,
};

/**
 * Busca global da navbar (GEN-3497, Fase 4).
 *
 * `Ctrl/Cmd + K` para quem já sabe o caminho, clique no campo para quem não sabe — as duas
 * portas para a mesma coisa (heurística 7 de Nielsen: acelerador para o experiente sem
 * penalizar o novato).
 *
 * A paleta consulta TRÊS origens em paralelo e mostra tudo numa lista só:
 *   - telas do sistema, resolvidas no cliente (não custa requisição e é metade do uso real);
 *   - empresas, certidões e Caixa Postal, via `GET /search/:guid_client`;
 *   - cards do Kanban 2.0, via o `/kanban2/search` que o próprio módulo mantém — a
 *     visibilidade de board privado mora lá e não pode ser reimplementada aqui.
 *
 * Feita com Modal do Chakra, e não com o `cmdk`/Radix que existe em `ui/lovable`: todo
 * overlay do produto é Chakra, e Radix montado dentro de container Chakra já apareceu atrás
 * de modal por causa do z-index copiado no mount. A navegação por teclado é ~30 linhas.
 */
export const GlobalSearch = () => {
  const {
    validation,
    currentClient,
    hasCpfProduct,
    hasTaxCurrentAccountProduct,
    hasCertificate,
    collaborator,
    resources,
    resourcesLoaded,
  } = useAppData();
  const { requestNavigation } = useConfigGuard();
  const { isOpen, onOpen, onClose: closeDisclosure } = useDisclosure();

  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<ISearchGroup[]>([]);
  const [tasks, setTasks] = useState<IFlatRow[]>([]);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Número de série da consulta. Duas buscas podem estar em voo ao mesmo tempo (o debounce
  // corta a chamada, não a resposta), e a rede não garante que voltem na ordem em que
  // saíram: sem isto, digitar "cnd" e emendar "cnd mg" deixa a resposta de "cnd" — mais
  // lenta — aterrissar por último e sobrescrever a lista com resultados de um termo que
  // não está mais no campo.
  const searchGeneration = useRef(0);

  const destinations = useMemo(
    () =>
      buildDestinations({
        validation,
        currentClient,
        hasCpfProduct,
        hasTaxCurrentAccountProduct,
        hasCertificate,
        collaborator,
        resources: loadedResources({ resources, resourcesLoaded }),
      }),
    [
      validation,
      currentClient,
      hasCpfProduct,
      hasTaxCurrentAccountProduct,
      hasCertificate,
      collaborator,
      resources,
      resourcesLoaded,
    ]
  );

  // --- atalho de teclado global ---------------------------------------------
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);

  // --- consulta -------------------------------------------------------------
  const guidClient = currentClient?.guid_client;

  const runSearch = useCallback(
    async (query: string) => {
      const generation = ++searchGeneration.current;

      if (!guidClient || query.trim().length < MIN_TERM) {
        setGroups([]);
        setTasks([]);
        setLoading(false);
        return;
      }
      setLoading(true);

      // As duas chamadas correm juntas e são independentes: se o Kanban falhar (ou o
      // cliente nem tiver o produto), o resto da busca continua servindo.
      const [general, kanban] = await Promise.all([
        globalSearch(guidClient, query.trim(), 5),
        searchKanbanCards(guidClient, query.trim()).catch(() => null),
      ]);

      // Chegou tarde: já existe uma consulta mais nova (ou a paleta fechou). Escrever aqui
      // seria mostrar o resultado de um termo que o usuário já abandonou.
      if (generation !== searchGeneration.current) return;

      setGroups(general.status === 200 ? (general.response as { groups?: ISearchGroup[] })?.groups ?? [] : []);

      const cards = kanban && kanban.status === 200 ? kanban.response : null;
      setTasks(
        Array.isArray(cards)
          ? cards.slice(0, 5).map((card) => ({
              key: `task-${card.cardId}`,
              groupLabel: "Tarefas (Kanban 2.0)",
              title: card.title,
              subtitle: card.boardName,
              // GEN-3857: o link lia `card.id` (o endpoint devolve `cardId`) e usava `?card=`, que o
              // Kanban não reconhece — o clique abria, no máximo, o quadro. O endereço canônico da
              // atividade (GEN-3167) abre o quadro JÁ com o card aberto.
              path: card.boardId && card.cardId ? atividadeK2Path(card.boardId, card.cardId) : "/kanban-v2/quadros",
              icon: GROUP_ICON.tasks,
            }))
          : []
      );

      setLoading(false);
    },
    [guidClient]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, isOpen, runSearch]);

  // --- lista achatada (é sobre ela que a seta anda) --------------------------
  const rows: IFlatRow[] = useMemo(() => {
    const destinationRows: IFlatRow[] = filterDestinations(destinations, term).map((destination) => ({
      key: `dest-${destination.id}`,
      groupLabel: "Ir para",
      title: destination.title,
      path: destination.path,
      icon: GROUP_ICON.destinations,
    }));

    const dataRows: IFlatRow[] = groups.flatMap((group) =>
      group.items.map((item) => ({
        key: `${group.source}-${item.id}`,
        groupLabel: group.label,
        title: item.title,
        subtitle: item.subtitle,
        path: item.path,
        icon: GROUP_ICON[group.source] ?? Search,
      }))
    );

    return [...destinationRows, ...dataRows, ...tasks];
  }, [destinations, term, groups, tasks]);

  // Termo novo recomeça a seleção no topo: manter o índice antigo faria o Enter abrir
  // um resultado que já saiu da lista.
  useEffect(() => setActive(0), [term]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active]);

  // Fechar sempre limpa o termo: reabrir com a busca anterior na tela faz o usuário achar
  // que a paleta "lembrou" de algo, quando na verdade os resultados podem estar velhos.
  const onClose = useCallback(() => {
    closeDisclosure();
    setTerm("");
    // Limpar só o termo não bastava. `groups`/`tasks` continuavam preenchidos e, embora o
    // render esconda a lista enquanto o campo está vazio, `rows` NÃO ficava vazia: reabrir
    // a paleta e apertar Enter navegava para o primeiro resultado da busca ANTERIOR, que
    // não estava visível em lugar nenhum. Pelo mesmo motivo o `aria-activedescendant`
    // apontava para um id ausente do DOM, que é o tipo de coisa que quebra leitor de tela.
    setGroups([]);
    setTasks([]);
    setActive(0);
    // Invalida a consulta em voo: paleta fechada não recebe resposta atrasada.
    searchGeneration.current += 1;
    setLoading(false);
  }, [closeDisclosure]);

  const go = (path: string) => {
    onClose();
    requestNavigation(path);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Escape tratado AQUI, e não pelo `closeOnEsc` do Chakra: medido em 21/08/2026 no QA, o
    // keydown chega ao ModalContent sem ser cancelado e o fechamento nativo não dispara
    // (a página tem outros dialogs montados, e o gerenciador de modais do Chakra decide
    // quem é o topo). A paleta é 100% teclado — não pode depender disso.
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((current) => (rows.length ? (current + 1) % rows.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => (rows.length ? (current - 1 + rows.length) % rows.length : 0));
    } else if (event.key === "Enter" && rows[active]) {
      event.preventDefault();
      go(rows[active].path);
    }
  };

  const truncated = groups.some((group) => group.hasMore);
  const shortTerm = term.trim().length > 0 && term.trim().length < MIN_TERM;

  return (
    <>
      {/* Gatilho no estilo Notion: nasce recolhido (só a lupa) e vira campo quando o
          usuário chega nele — mouse, foco de teclado, ou sozinho quando a tela é larga o
          bastante para caber sem espremer a trilha. Um campo de 250px parado na barra
          competia com a trilha por um espaço que ela usa mais. */}
      <Tooltip label="Buscar (Ctrl+K)" openDelay={600}>
        <Flex
          as="button"
          type="button"
          onClick={onOpen}
          aria-label="Abrir busca global"
          align="center"
          gap="7px"
          h="30px"
          flexShrink={0}
          overflow="hidden"
          borderRadius="7px"
          border="1px solid"
          borderColor="transparent"
          backgroundColor="transparent"
          // Mesmo cinza dos outros ícones da faixa (#77777B). O token do tema não estava
          // resolvendo aqui e a lupa saía quase preta, destoando dos vizinhos.
          color="#77777B"
          // SEMPRE recolhido por padrão — só a lupa. A primeira tentativa abria sozinho a
          // partir de 1536px, e nesse monitor (1920) ele nunca recolhia: o padrão que o
          // Thiago pediu simplesmente não aparecia. Quem abre é o usuário, chegando nele.
          w="30px"
          px="0"
          justifyContent="center"
          // Tudo por `sx` com seletor CSS real: as props `_hover`/`_focusVisible` deste app
          // emitem a regra `:hover` VAZIA (medido em 22/08/2026) — era por isso que a busca
          // nunca crescia ao passar o mouse, só no breakpoint.
          sx={{
            transition:
              "width 180ms ease, background-color 180ms ease, border-color 180ms ease, padding 180ms ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            // `display: none`, e não `opacity: 0`: com opacidade o rótulo e o selo continuam
            // ocupando 72px dentro de uma caixa de 30px, e o `justify-content: center`
            // empurrava a LUPA para fora — o `overflow: hidden` a cortava e o botão
            // aparecia vazio. Medido no navegador: ícone em x=1659, caixa em x=1702.
            ".topnav-search-label, .topnav-search-kbd": { display: "none" },
            "&:hover, &:focus-visible": {
              width: "230px",
              paddingLeft: "9px",
              paddingRight: "9px",
              justifyContent: "flex-start",
              backgroundColor: "#FFFFFF",
              borderColor: "#E2E8F0",
              color: "#77777B",
            },
            "&:hover .topnav-search-label, &:hover .topnav-search-kbd": { display: "block" },
            "&:focus-visible": { boxShadow: "0 0 0 2px #375CA9", outline: "none" },
            "&:focus-visible .topnav-search-label, &:focus-visible .topnav-search-kbd": { display: "block" },
          }}
        >
          <Box as="span" flexShrink={0} display="inline-flex">
            <Search size={16} />
          </Box>
          <Text className="topnav-search-label" fontSize="13px" whiteSpace="nowrap">
            Buscar
          </Text>
          <Box className="topnav-search-kbd" ml="auto" flexShrink={0}>
            <KeyCap>Ctrl K</KeyCap>
          </Box>
        </Flex>
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" isCentered={false}>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" pointerEvents="auto" />
        {/* GEN-3857 — `pointerEvents: auto` é obrigatório, como no SearchCNDModal (GEN-3536): com uma
            atividade do Kanban aberta, o painel (Radix) escreve `pointer-events: none` no `body` e a paleta,
            que vive no mesmo `body` via portal, herda o bloqueio. Ela aparecia (Ctrl+K funciona), mas o clique
            no resultado atravessava e caía no fundo do painel — que FECHAVA a atividade em vez de navegar. */}
        <ModalContent
          mt="80px"
          borderRadius="12px"
          overflow="hidden"
          zIndex={topNavLayers.panel}
          pointerEvents="auto"
          containerProps={{ pointerEvents: "auto" }}
        >
          <InputGroup>
            <InputLeftElement h="52px" pointerEvents="none">
              <Search size={18} color="#77777B" />
            </InputLeftElement>
            <Input
              autoFocus
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Buscar empresa, certidão, mensagem, tarefa ou tela"
              aria-label="Buscar em toda a plataforma"
              role="combobox"
              aria-expanded={rows.length > 0}
              aria-controls="topnav-search-results"
              aria-activedescendant={rows[active] ? `search-row-${rows[active].key}` : undefined}
              h="52px"
              fontSize="15px"
              border="none"
              borderBottom="1px solid"
              borderColor="#E2E8F0"
              borderRadius="0"
              _focusVisible={{ boxShadow: "none", borderColor: "#ACABAF" }}
            />
            {loading ? (
              <Flex position="absolute" right="16px" top="0" h="52px" align="center" zIndex={1}>
                <Spinner size="sm" color="#375CA9" />
              </Flex>
            ) : null}
          </InputGroup>

          <Box
            ref={listRef}
            id="topnav-search-results"
            role="listbox"
            aria-label="Resultados da busca"
            maxH="420px"
            overflowY="auto"
            className="scrollbarsidebar"
            py="6px"
          >
            {shortTerm ? (
              <Text px="16px" py="14px" fontSize="13px" color="#77777B">
                Digite pelo menos {MIN_TERM} caracteres.
              </Text>
            ) : term.trim().length === 0 ? (
              <Text px="16px" py="14px" fontSize="13px" color="#77777B">
                Busque por nome de empresa, CNPJ, tipo de certidão, assunto de mensagem, tarefa ou o nome de uma
                tela.
              </Text>
            ) : rows.length === 0 && !loading ? (
              <Text px="16px" py="14px" fontSize="13px" color="#77777B">
                Nada encontrado para “{term.trim()}”.
              </Text>
            ) : (
              rows.map((row, index) => {
                const Icon = row.icon;
                const isFirstOfGroup = index === 0 || rows[index - 1].groupLabel !== row.groupLabel;
                return (
                  <Box key={row.key}>
                    {isFirstOfGroup ? (
                      <Text px="16px" pt={index === 0 ? "6px" : "12px"} pb="4px" fontSize="11px" color="#77777B" fontWeight="600">
                        {row.groupLabel}
                      </Text>
                    ) : null}
                    <Flex
                      as="button"
                      type="button"
                      id={`search-row-${row.key}`}
                      role="option"
                      aria-selected={index === active}
                      data-active={index === active}
                      w="100%"
                      align="center"
                      gap="10px"
                      px="16px"
                      py="9px"
                      textAlign="left"
                      backgroundColor={index === active ? "#F2F0F4" : "transparent"}
                      _hover={{ backgroundColor: "#F2F0F4" }}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => go(row.path)}
                      aria-label={`${row.groupLabel}: ${row.title}`}
                    >
                      <Icon size={16} color="#77777B" />
                      <Flex direction="column" minW="0" flex="1">
                        <Text fontSize="14px" color="#303033" noOfLines={1}>
                          {row.title}
                        </Text>
                        {row.subtitle ? (
                          <Text fontSize="12px" color="#77777B" noOfLines={1}>
                            {row.subtitle}
                          </Text>
                        ) : null}
                      </Flex>
                    </Flex>
                  </Box>
                );
              })
            )}

            {/* Silêncio sobre o corte seria lido como "não existe mais nada". */}
            {truncated && rows.length > 0 ? (
              <Text px="16px" pt="10px" pb="4px" fontSize="11px" color="#77777B">
                Mostrando os primeiros resultados de cada tipo. Refine o termo para ver outros.
              </Text>
            ) : null}
          </Box>

          <Flex
            borderTop="1px solid"
            borderColor="#E2E8F0"
            px="16px"
            py="8px"
            gap="14px"
            fontSize="11px"
            color="#77777B"
          >
            <Text>
              <KeyCap>↑</KeyCap> <KeyCap>↓</KeyCap> navegar
            </Text>
            <Text>
              <KeyCap>Enter</KeyCap> abrir
            </Text>
            <Text>
              <KeyCap>Esc</KeyCap> fechar
            </Text>
          </Flex>
        </ModalContent>
      </Modal>
    </>
  );
};
